// src/odoo/customers/components/CustomerProductsPurchasedTable.tsx
// All comments are in English.

import React from "react";
import { PackageSearch } from "lucide-react";
import { formatHKD } from "./utils";

/**
 * Helper to format percentage values (0-1) into xx%.
 */
const formatPercent = (value: number | null | undefined): string => {
  if (value == null) return "-";
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * Helper to format ISO date string into YYYY-MM-DD display.
 */
const formatDateSafe = (value: string | null | undefined): string => {
  if (!value) return "-";
  return value.slice(0, 10);
};

export interface CustomerProductPurchaseRow {
  product_id: number;
  product_name: string;
  category_name?: string | null;
  gmv: number;
  share: number | null;
  order_count: number;
  quantity: number;
  first_purchase_date: string | null;
  last_purchase_date: string | null;
}

interface CustomerProductsPurchasedTableProps {
  products: CustomerProductPurchaseRow[];
}

/**
 * Left-side table: products purchased by this customer in the selected period.
 */
export const CustomerProductsPurchasedTable: React.FC<
  CustomerProductsPurchasedTableProps
> = ({ products }) => {
  const hasProducts = products && products.length > 0;

  return (
    <section className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <PackageSearch className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Products purchased</h2>
      </div>

      {!hasProducts ? (
        <p className="text-sm text-muted-foreground">
          No product lines in this period.
        </p>
      ) : (
        <div className="max-h-180 overflow-auto">
          <table className="min-w-full border-collapse text-xs">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-[11px] text-muted-foreground">
                <th className="px-2 py-1 text-left font-medium">Product</th>
                <th className="px-2 py-1 text-right font-medium">GMV</th>
                <th className="px-2 py-1 text-right font-medium">Share</th>
                <th className="px-2 py-1 text-right font-medium">Orders</th>
                <th className="px-2 py-1 text-right font-medium">Quantity</th>
                <th className="px-2 py-1 text-right font-medium">
                  First / Last
                </th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.product_id} className="border-b last:border-0">
                  <td className="px-2 py-1">
                    <div className="flex flex-col gap-0.5">
                      <span className="truncate text-xs font-medium">
                        {p.product_name}
                      </span>
                      {p.category_name && (
                        <span className="truncate text-[11px] text-muted-foreground">
                          {p.category_name}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">
                    {formatHKD(p.gmv)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">
                    {formatPercent(p.share)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">
                    {p.order_count}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right">
                    {p.quantity.toFixed(2)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1 text-right text-[11px] text-muted-foreground">
                    {formatDateSafe(p.first_purchase_date)} –{" "}
                    {formatDateSafe(p.last_purchase_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

/**
 * Right-side explanation card: teaching users how to read "Products purchased".
 * Intended to be placed next to CustomerProductsPurchasedTable in the layout.
 */
export const CustomerProductsPurchasedExplanation: React.FC = () => {
  return (
    <section className="rounded-lg border bg-card p-4 shadow-sm">
            <h2 className="mb-2 text-sm font-semibold">
          How to read: Products purchased
      </h2>
      <p className="text-xs leading-relaxed text-muted-foreground">
        This table summarizes every product the customer purchased within the selected
        period. Each row represents one SKU and provides insight into how important
        that product is to the customer's recent purchasing behaviour.

        <br /><br />
        <span className="font-semibold">• Product</span> — Shows the product name and, when
        available, the category it belongs to. This helps you quickly identify whether
        the customer focuses on specific categories or spreads purchases across diverse
        product groups.

        <br /><br />
        <span className="font-semibold">• GMV</span> — The total gross merchandise value
        spent on that product during the selected date range. Higher GMV items indicate
        key revenue contributors and may represent the customer’s operational priorities.

        <br /><br />
        <span className="font-semibold">• Share</span> — The percentage of this product’s
        GMV relative to the customer’s total GMV in the selected period. A high share
        indicates a high-dependency SKU, meaning stock-outs or price changes could have
        a strong impact on this customer.

        <br /><br />
        <span className="font-semibold">• Orders</span> — How many separate orders
        included this product. A high order count suggests frequent repeat purchases,
        while a low count but high GMV may indicate occasional but high-value buying
        patterns.

        <br /><br />
        <span className="font-semibold">• Quantity</span> — The total units purchased.
        When combined with Orders, this helps distinguish between:
        regular low-quantity usage (e.g., consumables) versus
        fewer but larger bulk purchases (e.g., equipment or licences).

        <br /><br />
        <span className="font-semibold">• First / Last</span> — The earliest and most
        recent dates this customer purchased the product. This helps evaluate purchase
        consistency:
        <ul className="list-disc pl-4 mt-1">
          <li>Short intervals between first and last purchases indicate ongoing demand.</li>
          <li>A very old “first” but very recent “last” may suggest reactivation.</li>
          <li>A recent “first” purchase might indicate cross-sell success or expanding needs.</li>
        </ul>

        <br />
        <span className="font-semibold">Interpretation tips:</span>
        <ul className="list-disc pl-4 mt-1">
          <li>
            Products with high Share and high Orders are core items for this customer.
          </li>
          <li>
            Products with low Share but high Orders often represent essential but low-value repeat consumables.
          </li>
          <li>
            Products with high GMV but only one order may indicate large one-off projects—or opportunities for upsell renewal.
          </li>
          <li>
            Long gaps between the First and Last dates may indicate seasonal or irregular purchasing patterns.
          </li>
        </ul>
      </p>

    </section>
  );
};
